import * as agentScannerService from '~/services/agentScannerService';
import { selectors as commonSelectors } from '~/domains/common';
import * as actions from './actionTypes';

/**
 * Agent Scanner Actions
 */

// List Actions
export function fetchAgentScannerListRequest() {
  return {
    type: actions.FETCH_AGENT_SCANNER_LIST_REQUEST
  };
}

export function fetchAgentScannerListSuccess(pageData) {
  return {
    type: actions.FETCH_AGENT_SCANNER_LIST_SUCCESS,
    pageData
  };
}

export function fetchAgentScannerListFailure(error) {
  return {
    type: actions.FETCH_AGENT_SCANNER_LIST_FAILURE,
    error
  };
}

// Create Actions
export function createAgentScannerSuccess(scanner) {
  return {
    type: actions.CREATE_AGENT_SCANNER_SUCCESS,
    scanner
  };
}

// Update Actions
export function updateAgentScannerSuccess(scanner) {
  return {
    type: actions.UPDATE_AGENT_SCANNER_SUCCESS,
    scanner
  };
}

// Scanner Runs Actions
export function fetchScannerRunsRequest(scannerId) {
  return {
    type: actions.FETCH_SCANNER_RUNS_REQUEST,
    scannerId
  };
}

export function fetchScannerRunsSuccess(scannerId, pageData) {
  return {
    type: actions.FETCH_SCANNER_RUNS_SUCCESS,
    scannerId,
    pageData
  };
}

export function fetchScannerRunsFailure(scannerId, error) {
  return {
    type: actions.FETCH_SCANNER_RUNS_FAILURE,
    scannerId,
    error
  };
}

export function clearScannerRuns() {
  return {
    type: actions.CLEAR_SCANNER_RUNS
  };
}

export function runScanConfigRequest() {
  return {
    type: actions.RUN_SCAN_CONFIG_REQUEST
  };
}

// Thunk Actions (Async Operations)

/**
 * Load agent scanners list
 * @param {string} organizationId - Organization ID
 * @param {number} page - Page number (default: 0)
 * @param {number} size - Page size (default: 10)
 * @returns {Function} Thunk function
 */
export function loadAgentScanners(organizationId, page = 0, size = 10) {
  return (dispatch, getState) => {
    const state = getState();
    const context = commonSelectors.context(state);

    dispatch(fetchAgentScannerListRequest());

    return agentScannerService
      .list({ organizationId, context, page, size })
      .then((pageData) => dispatch(fetchAgentScannerListSuccess(pageData)))
      .catch((error) => {
        dispatch(fetchAgentScannerListFailure(error));
        throw error;
      });
  };
}

/**
 * Create a new agent scanner
 * @param {string} organizationId - Organization ID
 * @param {Object} scannerData - Scanner configuration data
 * @returns {Function} Thunk function
 */
export function createAgentScanner(organizationId, scannerData) {
  return (dispatch, getState) => {
    const state = getState();
    const context = commonSelectors.context(state);

    return agentScannerService
      .create({ organizationId, context, data: scannerData })
      .then((response) => {
        dispatch(createAgentScannerSuccess(response));

        return response;
      })
      .catch((error) => {
        throw error;
      });
  };
}

/**
 * Update an existing agent scanner
 * @param {string} organizationId - Organization ID
 * @param {string} scannerId - Scanner ID
 * @param {Object} scannerData - Scanner configuration data
 * @returns {Function} Thunk function
 */
export function updateAgentScanner(organizationId, scannerId, scannerData) {
  return (dispatch, getState) => {
    const state = getState();
    const context = commonSelectors.context(state);

    return agentScannerService
      .update({
        organizationId,
        scannerId,
        context,
        data: scannerData
      })
      .then((response) => {
        dispatch(updateAgentScannerSuccess(response));

        return response;
      })
      .catch((error) => {
        throw error;
      });
  };
}

/**
 * Load scanner runs (history) for a specific scanner
 * @param {string} organizationId - Organization ID
 * @param {string} scannerId - Scanner ID
 * @param {number} page - Page number (default: 0)
 * @param {number} size - Page size (default: 20)
 * @returns {Function} Thunk function
 */
export function loadScannerRuns(
  organizationId,
  scannerId,
  page = 0,
  size = 20
) {
  return (dispatch, getState) => {
    const state = getState();
    const context = commonSelectors.context(state);

    dispatch(fetchScannerRunsRequest(scannerId));

    return agentScannerService
      .listRuns({ scannerId, organizationId, context, page, size })
      .then((pageData) =>
        dispatch(fetchScannerRunsSuccess(scannerId, pageData))
      )
      .catch((error) => {
        dispatch(fetchScannerRunsFailure(scannerId, error));
        throw error;
      });
  };
}

/**
 * Run an existing scan configuration
 * @param {string} organizationId - Organization ID
 * @param {string} scannerId - Scanner ID
 * @returns {Function} Thunk function
 */
export function runScanConfiguration(organizationId, scannerId) {
  return (dispatch, getState) => {
    const state = getState();
    const context = commonSelectors.context(state);

    return agentScannerService
      .execute({
        organizationId,
        scannerId,
        context
      })
      .then((response) => {
        dispatch(runScanConfigRequest());

        return response;
      })
      .catch((error) => {
        throw error;
      });
  };
}
